/**
 * CoachingMeetings — biweekly Thursday 3pm business meetings.
 *
 * Replaces external coaching (Kyle). Meetings live as collapsible cards;
 * top of the page is a goals dashboard (monthly/quarterly/yearly tabs).
 * Each meeting renders a 7-section template: Wins, KPI Review (live data),
 * Goal Check-In, Action Items Review, Discussion, New Action Items, Notes.
 */
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSession } from "../App";
import { apiFetch } from "../lib/apiFetch";

const API = "/api/coaching";
const CLAUDE_API = "/api/claude";

// ── Period helpers ────────────────────────────────────────────────────────
function todayUTC() {
  const d = new Date();
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
}
function fmtDateLong(iso) {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("en-CA", {
    weekday: "long", month: "long", day: "numeric", year: "numeric", timeZone: "UTC",
  });
}
function fmtDateShort(iso) {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("en-CA", {
    month: "short", day: "numeric", year: "numeric", timeZone: "UTC",
  });
}
function currentMonthlyPeriod() {
  const d = todayUTC();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}
function currentQuarterlyPeriod() {
  const d = todayUTC();
  const q = Math.floor(d.getUTCMonth() / 3) + 1;
  return `${d.getUTCFullYear()}-Q${q}`;
}
function currentYearlyPeriod() {
  return String(todayUTC().getUTCFullYear());
}
function fmtMoney(n) {
  const v = Number(n || 0);
  if (!isFinite(v)) return "—";
  return v.toLocaleString("en-CA", { style: "currency", currency: "CAD", maximumFractionDigits: 0 });
}
function fmtDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return "—";
  return d.toLocaleString("en-CA", {
    month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
  });
}
// Whole days between an ISO timestamp and now (null if unparseable).
function daysAgo(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d)) return null;
  return Math.floor((Date.now() - d.getTime()) / 86400000);
}

// ── Status pill colors ────────────────────────────────────────────────────
const GOAL_STATUS_COLOR = {
  on_track: { bg: "rgba(165,206,58,0.12)", fg: "#a5ce3a", label: "On Track" },
  behind:   { bg: "rgba(245,158,11,0.12)", fg: "#fbbf24", label: "Behind" },
  achieved: { bg: "rgba(34,197,94,0.15)",  fg: "#86efac", label: "Achieved" },
  dropped:  { bg: "rgba(255,255,255,0.05)", fg: "#8892a4", label: "Dropped" },
};
const ITEM_STATUS_COLOR = {
  open:        { bg: "rgba(96,165,250,0.12)", fg: "#93c5fd", label: "Open" },
  in_progress: { bg: "rgba(245,158,11,0.12)", fg: "#fbbf24", label: "In Progress" },
  done:        { bg: "rgba(34,197,94,0.15)",  fg: "#86efac", label: "Done" },
  blocked:     { bg: "rgba(239,68,68,0.12)",  fg: "#fca5a5", label: "Blocked" },
};
const MEETING_STATUS_COLOR = {
  upcoming:  { bg: "rgba(96,165,250,0.12)", fg: "#93c5fd", label: "Upcoming" },
  completed: { bg: "rgba(34,197,94,0.15)",  fg: "#86efac", label: "Completed" },
  cancelled: { bg: "rgba(239,68,68,0.12)",  fg: "#fca5a5", label: "Cancelled" },
};

// ═════════════════════════════════════════════════════════════════════════
export default function CoachingMeetings() {
  const navigate = useNavigate();
  const { session } = useSession() || {};
  const userEmail = session?.user?.email?.toLowerCase() || "";

  const [meetings, setMeetings]   = useState([]);
  const [goals, setGoals]         = useState([]);
  const [actionItems, setActionItems] = useState([]);
  const [kpis, setKpis]           = useState(null);
  const [busy, setBusy]           = useState("");
  const [error, setError]         = useState("");
  const [status, setStatus]       = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [didSetup, setDidSetup]   = useState(false);

  // Meeting-notes ingestion state
  const [meetingTypes, setMeetingTypes] = useState([]);
  const [meetingNotes, setMeetingNotes] = useState([]);
  const [showNoteModal, setShowNoteModal] = useState(false);

  // ── Boot ──────────────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        setBusy("loading");
        // Run setup once to ensure tables exist + seed initial meetings
        if (!didSetup) {
          await fetch(API, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ action: "setup" }),
          });
          setDidSetup(true);
        }
        await refreshAll();
      } catch (e) {
        setError(e.message);
      } finally {
        setBusy("");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshAll = useCallback(async () => {
    const [m, g, a, k, t, n] = await Promise.all([
      fetch(`${API}?action=list-meetings`).then(r => r.json()),
      fetch(`${API}?action=list-goals`).then(r => r.json()),
      fetch(`${API}?action=list-action-items`).then(r => r.json()),
      fetch(`${API}?action=kpis`).then(r => r.json()),
      fetch(`${API}?action=list-meeting-types`).then(r => r.json()),
      fetch(`${API}?action=list-meeting-notes&limit=100`).then(r => r.json()),
    ]);
    setMeetings(m.meetings || []);
    setGoals(g.goals || []);
    setActionItems(a.items || []);
    setKpis(k.ok ? k : null);
    setMeetingTypes(t.types || []);
    setMeetingNotes(n.notes || []);

    // Default-expand the next upcoming meeting if nothing is expanded yet
    setExpandedId(prev => {
      if (prev) return prev;
      const today = todayUTC().toISOString().slice(0, 10);
      const nextUp = (m.meetings || [])
        .filter(x => x.meeting_date >= today && x.status === "upcoming")
        .sort((a, b) => a.meeting_date.localeCompare(b.meeting_date))[0];
      return nextUp?.id || null;
    });
  }, []);

  // ── Sync: re-pull live sources before a meeting ──────────────────────────
  // Jobs / reviews / pending-invoices are read live by the KPI endpoint, so a
  // plain refresh already picks them up. The QuickBooks financial snapshot is
  // the one cached source — re-pull it for the current month, then refresh all.
  const syncData = useCallback(async () => {
    setBusy("syncing"); setError(""); setStatus("");
    const period = currentMonthlyPeriod();
    let qbMsg;
    try {
      const r = await apiFetch("/api/coach", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "refresh-snapshot", period }),
      }).then(r => r.json());
      qbMsg = r.ok
        ? `QuickBooks financials refreshed for ${period}.`
        : `QuickBooks refresh failed: ${r.error || "unknown error"}.`;
    } catch (e) {
      qbMsg = `QuickBooks refresh failed: ${e.message}.`;
    }
    try {
      await refreshAll();
    } catch (e) {
      setError(e.message);
    }
    setStatus(`${qbMsg} KPIs updated at ${new Date().toLocaleTimeString("en-CA", { hour: "numeric", minute: "2-digit" })}.`);
    setBusy("");
  }, [refreshAll]);

  // ── Goal handlers ─────────────────────────────────────────────────────
  const upsertGoal = useCallback(async (payload) => {
    setBusy("saving"); setError("");
    try {
      const r = await fetch(API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "upsert-goal", ...payload }),
      }).then(r => r.json());
      if (!r.ok) throw new Error(r.error || "Save failed");
      const g = await fetch(`${API}?action=list-goals`).then(r => r.json());
      setGoals(g.goals || []);
    } catch (e) { setError(e.message); }
    finally { setBusy(""); }
  }, []);

  const deleteGoal = useCallback(async (id) => {
    if (!window.confirm("Delete this goal?")) return;
    setBusy("saving"); setError("");
    try {
      await fetch(API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "delete-goal", id }),
      });
      setGoals(prev => prev.filter(g => g.id !== id));
    } catch (e) { setError(e.message); }
    finally { setBusy(""); }
  }, []);

  // ── Action item handlers ──────────────────────────────────────────────
  const upsertActionItem = useCallback(async (payload) => {
    setBusy("saving"); setError("");
    try {
      const r = await fetch(API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "upsert-action-item", ...payload }),
      }).then(r => r.json());
      if (!r.ok) throw new Error(r.error || "Save failed");
      const a = await fetch(`${API}?action=list-action-items`).then(r => r.json());
      setActionItems(a.items || []);
    } catch (e) { setError(e.message); }
    finally { setBusy(""); }
  }, []);

  const deleteActionItem = useCallback(async (id) => {
    if (!window.confirm("Delete this action item?")) return;
    setBusy("saving"); setError("");
    try {
      await fetch(API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "delete-action-item", id }),
      });
      setActionItems(prev => prev.filter(a => a.id !== id));
    } catch (e) { setError(e.message); }
    finally { setBusy(""); }
  }, []);

  // ── Meeting handlers ──────────────────────────────────────────────────
  const upsertMeeting = useCallback(async (payload) => {
    setBusy("saving"); setError("");
    try {
      const r = await fetch(API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "upsert-meeting", ...payload }),
      }).then(r => r.json());
      if (!r.ok) throw new Error(r.error || "Save failed");
      const m = await fetch(`${API}?action=list-meetings`).then(r => r.json());
      setMeetings(m.meetings || []);
      setStatus("Saved.");
    } catch (e) { setError(e.message); }
    finally { setBusy(""); }
  }, []);

  const addMeeting = useCallback(async () => {
    const today = todayUTC().toISOString().slice(0, 10);
    // Default to two weeks from the most recent upcoming
    const lastUpcoming = meetings
      .filter(m => m.status === "upcoming")
      .sort((a, b) => b.meeting_date.localeCompare(a.meeting_date))[0];
    const base = lastUpcoming?.meeting_date || today;
    const [y, mo, d] = base.split("-").map(Number);
    const next = new Date(Date.UTC(y, mo - 1, d));
    next.setUTCDate(next.getUTCDate() + 14);
    const newDate = next.toISOString().slice(0, 10);
    if (meetings.find(m => m.meeting_date === newDate)) {
      setError(`A meeting already exists on ${newDate}.`);
      return;
    }
    await upsertMeeting({ meeting_date: newDate });
  }, [meetings, upsertMeeting]);

  // ── Meeting-notes handlers ────────────────────────────────────────────
  const refreshMeetingNotes = useCallback(async () => {
    const n = await fetch(`${API}?action=list-meeting-notes&limit=100`).then(r => r.json());
    setMeetingNotes(n.notes || []);
  }, []);

  const refreshMeetingTypes = useCallback(async () => {
    const t = await fetch(`${API}?action=list-meeting-types`).then(r => r.json());
    setMeetingTypes(t.types || []);
  }, []);

  const createMeetingType = useCallback(async (name) => {
    const r = await fetch(API, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: "upsert-meeting-type", name }),
    }).then(r => r.json());
    if (!r.ok) throw new Error(r.error || "Could not create type");
    await refreshMeetingTypes();
    return r.type;
  }, [refreshMeetingTypes]);

  const deleteMeetingType = useCallback(async (id) => {
    if (!window.confirm("Delete this meeting type? (Built-in types and types in use cannot be deleted.)")) return;
    const r = await fetch(API, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: "delete-meeting-type", id }),
    }).then(r => r.json());
    if (!r.ok) {
      setError(r.error || "Could not delete type");
      return;
    }
    await refreshMeetingTypes();
  }, [refreshMeetingTypes]);

  const ingestMeetingNotes = useCallback(async (payload) => {
    const r = await fetch(API, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: "ingest-meeting-notes", ...payload, user_email: userEmail }),
    }).then(r => r.json());
    if (!r.ok) throw new Error(r.error || "Ingestion failed");
    await refreshMeetingNotes();
    // Also refresh meetings so the linked footer count updates
    const m = await fetch(`${API}?action=list-meetings`).then(r => r.json());
    setMeetings(m.meetings || []);
    return r;
  }, [refreshMeetingNotes, userEmail]);

  // ── Meeting groupings ────────────────────────────────────────────────
  const today = todayUTC().toISOString().slice(0, 10);
  const upcomingMeetings = useMemo(
    () => meetings.filter(m => m.meeting_date >= today)
                  .sort((a, b) => a.meeting_date.localeCompare(b.meeting_date)),
    [meetings, today]
  );
  const pastMeetings = useMemo(
    () => meetings.filter(m => m.meeting_date < today)
                  .sort((a, b) => b.meeting_date.localeCompare(a.meeting_date)),
    [meetings, today]
  );

  // ── Open action items (for carry-forward into upcoming meetings) ─────
  const openActionItems = useMemo(
    () => actionItems.filter(a => a.status !== "done"),
    [actionItems]
  );

  return (
    <div style={S.page}>
      {/* Header */}
      <div style={S.header}>
        <div>
          <button onClick={() => navigate("/coach")} style={S.backBtn}>‹ Back to Coach</button>
          <div style={S.title}>Business Meetings</div>
          <div style={S.subtitle}>Biweekly Thursdays at 3pm — replaces external coaching cadence.</div>
        </div>
        <div style={S.headerActions}>
          <button onClick={addMeeting} disabled={!!busy} style={S.btn}>+ Add Meeting</button>
          <button onClick={refreshAll} disabled={!!busy} style={S.btn}>
            {busy === "loading" ? "Loading…" : "Refresh"}
          </button>
          <button onClick={syncData} disabled={!!busy} style={S.btnPrimary} title="Re-pull QuickBooks financials and refresh all KPIs">
            {busy === "syncing" ? "Syncing…" : "↻ Sync data"}
          </button>
        </div>
      </div>

      {error && <div style={S.errorBanner}>{error}<button style={S.bannerClose} onClick={() => setError("")}>×</button></div>}
      {status && <div style={S.statusBanner}>{status}<button style={S.bannerClose} onClick={() => setStatus("")}>×</button></div>}

      {/* Goals dashboard — always visible at the top */}
      <GoalsDashboard
        goals={goals}
        onUpsert={upsertGoal}
        onDelete={deleteGoal}
        busy={busy}
      />

      {/* Meeting Notes ingestion — feed Jai-Man's brain */}
      <MeetingNotesCard
        notes={meetingNotes}
        types={meetingTypes}
        onOpenModal={() => setShowNoteModal(true)}
        onDeleteType={deleteMeetingType}
      />

      {showNoteModal && (
        <MeetingNotesModal
          types={meetingTypes}
          onCreateType={createMeetingType}
          onIngest={ingestMeetingNotes}
          onClose={() => setShowNoteModal(false)}
        />
      )}

      {/* Upcoming meetings */}
      <div style={S.sectionHeader}>
        <div style={S.sectionTitle}>Upcoming Meetings</div>
        <div style={S.sectionCount}>{upcomingMeetings.length}</div>
      </div>
      {upcomingMeetings.length === 0 && (
        <div style={S.emptyCard}>No upcoming meetings — click "+ Add Meeting" to schedule one.</div>
      )}
      {upcomingMeetings.map(m => (
        <MeetingCard
          key={m.id}
          meeting={m}
          expanded={expandedId === m.id}
          onToggle={() => setExpandedId(expandedId === m.id ? null : m.id)}
          actionItems={actionItems.filter(a => a.meeting_id === m.id)}
          linkedNotes={meetingNotes.filter(n => n.linked_meeting_id === m.id)}
          openCarryForward={openActionItems.filter(a => !a.meeting_id || a.meeting_id !== m.id)}
          goals={goals}
          kpis={kpis}
          onUpsertMeeting={upsertMeeting}
          onUpsertActionItem={upsertActionItem}
          onDeleteActionItem={deleteActionItem}
          onUpsertGoal={upsertGoal}
          busy={busy}
          isUpcoming
        />
      ))}

      {/* Past meetings — collapsed by default */}
      <div style={S.sectionHeader}>
        <div style={S.sectionTitle}>Past Meetings</div>
        <div style={S.sectionCount}>{pastMeetings.length}</div>
      </div>
      {pastMeetings.length === 0 && (
        <div style={S.emptyCard}>No past meetings yet.</div>
      )}
      {pastMeetings.map(m => (
        <MeetingCard
          key={m.id}
          meeting={m}
          expanded={expandedId === m.id}
          onToggle={() => setExpandedId(expandedId === m.id ? null : m.id)}
          actionItems={actionItems.filter(a => a.meeting_id === m.id)}
          linkedNotes={meetingNotes.filter(n => n.linked_meeting_id === m.id)}
          openCarryForward={[]}
          goals={goals}
          kpis={kpis}
          onUpsertMeeting={upsertMeeting}
          onUpsertActionItem={upsertActionItem}
          onDeleteActionItem={deleteActionItem}
          onUpsertGoal={upsertGoal}
          busy={busy}
          isUpcoming={false}
        />
      ))}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// Goals dashboard
// ═════════════════════════════════════════════════════════════════════════
function GoalsDashboard({ goals, onUpsert, onDelete, busy }) {
  const [tab, setTab] = useState("monthly");
  const [period, setPeriod] = useState(() => {
    if (tab === "yearly") return currentYearlyPeriod();
    if (tab === "quarterly") return currentQuarterlyPeriod();
    return currentMonthlyPeriod();
  });
  const [adding, setAdding] = useState(false);

  // When tab changes, snap to the current period for that tab type
  useEffect(() => {
    if (tab === "monthly")        setPeriod(currentMonthlyPeriod());
    else if (tab === "quarterly") setPeriod(currentQuarterlyPeriod());
    else                          setPeriod(currentYearlyPeriod());
  }, [tab]);

  const filtered = useMemo(
    () => goals.filter(g => g.goal_type === tab && g.period === period)
               .sort((a, b) => a.sort_order - b.sort_order || a.created_at.localeCompare(b.created_at)),
    [goals, tab, period]
  );

  // Available periods for this goal type (for the period selector)
  const availablePeriods = useMemo(() => {
    const set = new Set(goals.filter(g => g.goal_type === tab).map(g => g.period));
    if (tab === "monthly")        set.add(currentMonthlyPeriod());
    else if (tab === "quarterly") set.add(currentQuarterlyPeriod());
    else                          set.add(currentYearlyPeriod());
    return Array.from(set).sort().reverse();
  }, [goals, tab]);

  const achieved = filtered.filter(g => g.status === "achieved").length;
  const total    = filtered.length;

  return (
    <div style={S.card}>
      <div style={S.cardHeader}>
        <div style={S.cardTitle}>Goals</div>
        <div style={S.tabRow}>
          {["monthly", "quarterly", "yearly"].map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              style={tab === t ? S.tabActive : S.tab}
            >
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div style={S.goalsControls}>
        <select value={period} onChange={e => setPeriod(e.target.value)} style={S.select}>
          {availablePeriods.map(p => <option key={p} value={p}>{formatPeriodLabel(tab, p)}</option>)}
        </select>
        <div style={S.goalsProgress}>
          <span style={S.goalsProgressLabel}>{achieved}/{total} achieved</span>
          <div style={S.goalsProgressBar}>
            <div style={{
              ...S.goalsProgressFill,
              width: total > 0 ? `${(achieved / total) * 100}%` : "0%",
            }} />
          </div>
        </div>
        <button onClick={() => setAdding(true)} disabled={!!busy} style={S.btn}>+ Add Goal</button>
      </div>

      {adding && (
        <GoalEditor
          initial={{ goal_type: tab, period, status: "on_track" }}
          onSave={async (g) => { await onUpsert(g); setAdding(false); }}
          onCancel={() => setAdding(false)}
          busy={!!busy}
        />
      )}

      {filtered.length === 0 && !adding && (
        <div style={S.emptyText}>No goals for this period yet.</div>
      )}

      {filtered.map(g => (
        <GoalRow key={g.id} goal={g} onUpsert={onUpsert} onDelete={onDelete} busy={busy} />
      ))}
    </div>
  );
}

function formatPeriodLabel(type, period) {
  if (type === "monthly") {
    const [y, m] = period.split("-").map(Number);
    if (!y || !m) return period;
    return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString("en-CA", {
      month: "long", year: "numeric", timeZone: "UTC",
    });
  }
  if (type === "quarterly") return period.replace("-", " ");
  return period;
}

function GoalRow({ goal, onUpsert, onDelete, busy }) {
  const [editing, setEditing] = useState(false);
  if (editing) {
    return (
      <GoalEditor
        initial={goal}
        onSave={async (g) => { await onUpsert({ id: goal.id, ...g }); setEditing(false); }}
        onCancel={() => setEditing(false)}
        busy={!!busy}
      />
    );
  }
  const sc = GOAL_STATUS_COLOR[goal.status] || GOAL_STATUS_COLOR.on_track;
  return (
    <div style={S.goalRow}>
      <div style={S.goalMain}>
        <div style={S.goalDesc}>{goal.description}</div>
        <div style={S.goalMeta}>
          {goal.target && <span><strong>Target:</strong> {goal.target}</span>}
          {goal.current_value && <span><strong>Current:</strong> {goal.current_value}</span>}
          {goal.notes && <span style={{ color: "#8892a4" }}>· {goal.notes}</span>}
        </div>
      </div>
      <div style={S.goalActions}>
        <span style={{ ...S.statusPill, background: sc.bg, color: sc.fg }}>{sc.label}</span>
        <button onClick={() => setEditing(true)} style={S.linkBtn}>Edit</button>
        <button onClick={() => onDelete(goal.id)} style={S.linkBtnDanger}>Delete</button>
      </div>
    </div>
  );
}

function GoalEditor({ initial, onSave, onCancel, busy }) {
  const [f, setF] = useState({
    goal_type:     initial.goal_type || "monthly",
    period:        initial.period || currentMonthlyPeriod(),
    description:   initial.description || "",
    target:        initial.target || "",
    current_value: initial.current_value || "",
    status:        initial.status || "on_track",
    notes:         initial.notes || "",
  });
  const update = (k, v) => setF(prev => ({ ...prev, [k]: v }));
  return (
    <div style={S.editorRow}>
      <div style={S.editorGrid}>
        <input
          placeholder="Goal description"
          value={f.description}
          onChange={e => update("description", e.target.value)}
          style={{ ...S.input, gridColumn: "1 / -1" }}
        />
        <input placeholder="Target (e.g. $200k revenue)" value={f.target}
               onChange={e => update("target", e.target.value)} style={S.input} />
        <input placeholder="Current value" value={f.current_value}
               onChange={e => update("current_value", e.target.value)} style={S.input} />
        <select value={f.status} onChange={e => update("status", e.target.value)} style={S.select}>
          <option value="on_track">On Track</option>
          <option value="behind">Behind</option>
          <option value="achieved">Achieved</option>
          <option value="dropped">Dropped</option>
        </select>
        <input placeholder="Period (YYYY-MM, YYYY-Q1, YYYY)" value={f.period}
               onChange={e => update("period", e.target.value)} style={S.input} />
        <input placeholder="Notes" value={f.notes}
               onChange={e => update("notes", e.target.value)}
               style={{ ...S.input, gridColumn: "1 / -1" }} />
      </div>
      <div style={S.editorActions}>
        <button onClick={onCancel} disabled={busy} style={S.btn}>Cancel</button>
        <button
          onClick={() => f.description.trim() && onSave(f)}
          disabled={busy || !f.description.trim()}
          style={S.btnPrimary}
        >
          Save
        </button>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// Meeting card
// ═════════════════════════════════════════════════════════════════════════
function MeetingCard({
  meeting, expanded, onToggle,
  actionItems, openCarryForward, goals, kpis,
  onUpsertMeeting, onUpsertActionItem, onDeleteActionItem, onUpsertGoal,
  linkedNotes,
  busy, isUpcoming,
}) {
  const [wins, setWins]             = useState(meeting.wins || "");
  const [discussion, setDiscussion] = useState(meeting.discussion || "");
  const [notes, setNotes]           = useState(meeting.notes || "");
  const [savingField, setSavingField] = useState(null);

  // Sync local state if meeting record changes externally
  useEffect(() => { setWins(meeting.wins || ""); }, [meeting.wins]);
  useEffect(() => { setDiscussion(meeting.discussion || ""); }, [meeting.discussion]);
  useEffect(() => { setNotes(meeting.notes || ""); }, [meeting.notes]);

  const saveField = async (field, value) => {
    setSavingField(field);
    await onUpsertMeeting({ id: meeting.id, [field]: value });
    setSavingField(null);
  };

  const sc = MEETING_STATUS_COLOR[meeting.status] || MEETING_STATUS_COLOR.upcoming;

  return (
    <div style={S.meetingCard}>
      {/* Card header — collapsed view */}
      <div style={S.meetingHead} onClick={onToggle}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1, minWidth: 0 }}>
          <div style={S.chevron}>{expanded ? "▾" : "▸"}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={S.meetingDate}>{fmtDateLong(meeting.meeting_date)}</div>
            <div style={S.meetingSubdate}>3:00 PM · biweekly cadence</div>
          </div>
        </div>
        <span style={{ ...S.statusPill, background: sc.bg, color: sc.fg }}>{sc.label}</span>
      </div>

      {!expanded && actionItems.length > 0 && (
        <div style={S.meetingFootHint}>
          {actionItems.filter(a => a.status === "done").length}/{actionItems.length} action items done
        </div>
      )}

      {!expanded && linkedNotes && linkedNotes.length > 0 && (
        <div style={S.meetingFootHint}>
          📎 {linkedNotes.length} meeting note{linkedNotes.length === 1 ? "" : "s"} fed to Jai-Man (
          {linkedNotes.reduce((s, n) => s + (n.memories_extracted || 0), 0)} memories)
        </div>
      )}

      {expanded && (
        <div style={S.meetingBody}>
          {/* Status switcher */}
          <div style={S.statusRow}>
            <span style={S.label}>Meeting Status:</span>
            {["upcoming", "completed", "cancelled"].map(s => (
              <button
                key={s}
                onClick={() => onUpsertMeeting({ id: meeting.id, status: s })}
                disabled={!!busy}
                style={meeting.status === s ? S.chipActive : S.chip}
              >
                {MEETING_STATUS_COLOR[s].label}
              </button>
            ))}
          </div>

          {/* 1. Wins & Celebrations */}
          <Section title="1. Wins & Celebrations" minutes={5}>
            <textarea
              placeholder="What went well since last meeting? Team wins to celebrate?"
              value={wins}
              onChange={e => setWins(e.target.value)}
              onBlur={() => wins !== (meeting.wins || "") && saveField("wins", wins)}
              style={S.textarea}
              rows={3}
            />
            {savingField === "wins" && <div style={S.savingHint}>Saving…</div>}
          </Section>

          {/* 2. KPI Review */}
          <Section title="2. KPI Review" minutes={10}>
            <KpiPanel kpis={kpis} />
          </Section>

          {/* 3. Goal Check-In */}
          <Section title="3. Goal Check-In" minutes={10}>
            <GoalCheckIn goals={goals} onUpsertGoal={onUpsertGoal} busy={busy} />
          </Section>

          {/* 4. Action Items Review (carry-forward + this meeting's done/in-progress) */}
          <Section title="4. Action Items Review" minutes={5}>
            <ActionItemsReview
              meetingId={meeting.id}
              meetingItems={actionItems}
              openCarryForward={openCarryForward}
              onUpsert={onUpsertActionItem}
              onDelete={onDeleteActionItem}
              busy={busy}
              showCarryForward={isUpcoming}
            />
          </Section>

          {/* 5. Discussion Topics */}
          <Section title="5. Discussion Topics" minutes={15}>
            <textarea
              placeholder="Agenda items — pre-fill before the meeting, capture during."
              value={discussion}
              onChange={e => setDiscussion(e.target.value)}
              onBlur={() => discussion !== (meeting.discussion || "") && saveField("discussion", discussion)}
              style={S.textarea}
              rows={4}
            />
            {savingField === "discussion" && <div style={S.savingHint}>Saving…</div>}
          </Section>

          {/* 6. New Action Items */}
          <Section title="6. New Action Items" minutes={5}>
            <NewActionItem
              meetingId={meeting.id}
              onAdd={onUpsertActionItem}
              busy={busy}
            />
          </Section>

          {/* 7. Meeting Notes */}
          <Section title="7. Meeting Notes">
            <textarea
              placeholder="General notes, takeaways, decisions…"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              onBlur={() => notes !== (meeting.notes || "") && saveField("notes", notes)}
              style={S.textarea}
              rows={5}
            />
            {savingField === "notes" && <div style={S.savingHint}>Saving…</div>}
          </Section>

          {/* Linked Jai-Man meeting notes (ingested transcripts → extracted memories) */}
          {linkedNotes && linkedNotes.length > 0 && (
            <Section title="📎 Notes fed to Jai-Man">
              {linkedNotes.map(n => (
                <MeetingNoteRow key={n.id} note={n} compact />
              ))}
            </Section>
          )}
        </div>
      )}
    </div>
  );
}

function Section({ title, minutes, children }) {
  return (
    <div style={S.section}>
      <div style={S.sectionHead}>
        <div style={S.sectionTitleSm}>{title}</div>
        {minutes && <div style={S.sectionMinutes}>{minutes} min</div>}
      </div>
      {children}
    </div>
  );
}

// ── KPI Panel ────────────────────────────────────────────────────────────
function KpiPanel({ kpis }) {
  if (!kpis) {
    return <div style={S.emptyText}>Loading KPIs…</div>;
  }
  const j = kpis.jobs_last_14d   || {};
  const r = kpis.reviews_last_30d || {};
  const p = kpis.pending_invoices || {};
  const f = kpis.financial || null;
  const finAge   = daysAgo(f?.refreshed_at);
  const finStale = finAge != null && finAge >= 3;

  return (
    <div style={S.kpiGrid}>
      <Kpi label="Jobs (last 14d)"  value={j.count ?? "—"}
           sub={`Revenue ${fmtMoney(j.revenue || 0)} · Profit ${fmtMoney(j.profit || 0)}`} />
      <Kpi label="Avg Margin (14d)" value={j.avg_margin != null ? `${(j.avg_margin * 100).toFixed(1)}%` : "—"}
           sub={`Across ${j.count || 0} closed jobs`} />
      <Kpi label="Reviews (30d new)" value={r.count ?? "—"}
           sub={r.all_time_avg != null
             ? `${r.all_time_avg.toFixed(1)}★ all-time (${r.all_time_count}) · last ${r.last_review_date || "—"}`
             : `Avg ${r.avg_rating?.toFixed(2) || "—"} · ${r.five_star_count || 0} five-star`} />
      <Kpi label="Pending Invoices" value={p.count ?? "—"}
           sub={`Total ${fmtMoney(p.total || 0)}`} />
      {f && (
        <Kpi label={`YTD Revenue (${f.period || "latest"})`}
             value={fmtMoney(f.ytd_revenue || 0)}
             sub={`Cash ${fmtMoney(f.cash || 0)} · AR ${fmtMoney(f.ar || 0)}`} />
      )}
      {!f && (
        <Kpi label="Financial Snapshot" value="—" sub="Click Sync to pull from QuickBooks" />
      )}
      {j.top_jobs?.length > 0 && (
        <div style={{ ...S.kpiCard, gridColumn: "1 / -1" }}>
          <div style={S.kpiLabel}>Top Closed Jobs (last 14d)</div>
          <table style={S.miniTable}>
            <thead><tr>
              <th style={S.miniTh}>Job</th>
              <th style={S.miniTh}>Client</th>
              <th style={{ ...S.miniTh, textAlign: "right" }}>Revenue</th>
              <th style={{ ...S.miniTh, textAlign: "right" }}>Profit</th>
              <th style={{ ...S.miniTh, textAlign: "right" }}>Margin</th>
            </tr></thead>
            <tbody>
              {j.top_jobs.map((row, i) => (
                <tr key={i}>
                  <td style={S.miniTd}>#{row.job_num || "—"}</td>
                  <td style={S.miniTd}>{row.client || "—"}</td>
                  <td style={{ ...S.miniTd, textAlign: "right" }}>{fmtMoney(row.revenue)}</td>
                  <td style={{ ...S.miniTd, textAlign: "right" }}>{fmtMoney(row.profit)}</td>
                  <td style={{ ...S.miniTd, textAlign: "right" }}>{row.profit_pct ? `${row.profit_pct.toFixed(0)}%` : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div style={S.kpiFreshness}>
        <span>Jobs · reviews · invoices are live as of {fmtDateTime(kpis.generated_at)}.</span>
        {f && (
          <span style={finStale ? S.kpiStale : undefined}>
            {finStale ? "⚠ " : ""}QuickBooks financials as of {fmtDateTime(f.refreshed_at)}
            {finStale ? ` (${finAge}d old — click Sync)` : ""}.
          </span>
        )}
      </div>
    </div>
  );
}

function Kpi({ label, value, sub }) {
  return (
    <div style={S.kpiCard}>
      <div style={S.kpiLabel}>{label}</div>
      <div style={S.kpiValue}>{value}</div>
      {sub && <div style={S.kpiSub}>{sub}</div>}
    </div>
  );
}

// ── Goal check-in (compact list of all current-period goals) ─────────────
function GoalCheckIn({ goals, onUpsertGoal, busy }) {
  const monthly   = goals.filter(g => g.goal_type === "monthly"   && g.period === currentMonthlyPeriod());
  const quarterly = goals.filter(g => g.goal_type === "quarterly" && g.period === currentQuarterlyPeriod());
  const yearly    = goals.filter(g => g.goal_type === "yearly"    && g.period === currentYearlyPeriod());

  if (!monthly.length && !quarterly.length && !yearly.length) {
    return <div style={S.emptyText}>No active goals — add some at the top of the page.</div>;
  }

  const Group = ({ title, list }) => list.length > 0 && (
    <div style={{ marginBottom: 12 }}>
      <div style={S.checkInGroupTitle}>{title}</div>
      {list.map(g => (
        <div key={g.id} style={S.checkInRow}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={S.checkInDesc}>{g.description}</div>
            {(g.target || g.current_value) && (
              <div style={S.checkInMeta}>
                {g.target && `Target: ${g.target}`}{g.target && g.current_value && " · "}
                {g.current_value && `Current: ${g.current_value}`}
              </div>
            )}
          </div>
          <select
            value={g.status}
            onChange={e => onUpsertGoal({ id: g.id, status: e.target.value })}
            disabled={!!busy}
            style={S.selectSm}
          >
            <option value="on_track">On Track</option>
            <option value="behind">Behind</option>
            <option value="achieved">Achieved</option>
            <option value="dropped">Dropped</option>
          </select>
        </div>
      ))}
    </div>
  );

  return (
    <div>
      <Group title="MONTHLY" list={monthly} />
      <Group title="QUARTERLY" list={quarterly} />
      <Group title="YEARLY" list={yearly} />
    </div>
  );
}

// ── Action items review ──────────────────────────────────────────────────
function ActionItemsReview({
  meetingId, meetingItems, openCarryForward, onUpsert, onDelete, busy, showCarryForward,
}) {
  const carryToHere = async (item) => {
    await onUpsert({ id: item.id, meeting_id: meetingId });
  };

  return (
    <div>
      {showCarryForward && openCarryForward.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div style={S.checkInGroupTitle}>CARRY FORWARD ({openCarryForward.length} open)</div>
          {openCarryForward.map(item => (
            <ActionItemRow
              key={item.id}
              item={item}
              onUpsert={onUpsert}
              onDelete={onDelete}
              extraAction={
                <button onClick={() => carryToHere(item)} disabled={!!busy} style={S.linkBtn}>
                  → Move to this meeting
                </button>
              }
              busy={busy}
            />
          ))}
        </div>
      )}

      {meetingItems.length === 0 ? (
        <div style={S.emptyText}>No action items on this meeting.</div>
      ) : (
        meetingItems.map(item => (
          <ActionItemRow
            key={item.id}
            item={item}
            onUpsert={onUpsert}
            onDelete={onDelete}
            busy={busy}
          />
        ))
      )}
    </div>
  );
}

function ActionItemRow({ item, onUpsert, onDelete, busy, extraAction }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({
    description: item.description, owner: item.owner || "", due_date: item.due_date || "",
  });
  useEffect(() => {
    if (!editing) {
      setDraft({ description: item.description, owner: item.owner || "", due_date: item.due_date || "" });
    }
  }, [item, editing]);

  const sc = ITEM_STATUS_COLOR[item.status] || ITEM_STATUS_COLOR.open;

  if (editing) {
    return (
      <div style={S.actionRowEditing}>
        <input value={draft.description} onChange={e => setDraft({ ...draft, description: e.target.value })}
               style={{ ...S.input, gridColumn: "1 / -1" }} placeholder="Description" />
        <input value={draft.owner} onChange={e => setDraft({ ...draft, owner: e.target.value })}
               style={S.input} placeholder="Owner (Dylan / Colton)" />
        <input type="date" value={draft.due_date}
               onChange={e => setDraft({ ...draft, due_date: e.target.value })}
               style={S.input} />
        <div style={{ display: "flex", gap: 6, gridColumn: "1 / -1", justifyContent: "flex-end" }}>
          <button onClick={() => setEditing(false)} disabled={busy} style={S.btn}>Cancel</button>
          <button
            onClick={async () => {
              if (!draft.description.trim()) return;
              await onUpsert({ id: item.id, ...draft, due_date: draft.due_date || null });
              setEditing(false);
            }}
            disabled={busy}
            style={S.btnPrimary}
          >Save</button>
        </div>
      </div>
    );
  }

  return (
    <div style={S.actionRow}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={S.actionDesc}>{item.description}</div>
        <div style={S.actionMeta}>
          {item.owner && <span><strong>{item.owner}</strong></span>}
          {item.due_date && <span>· Due {fmtDateShort(item.due_date)}</span>}
        </div>
      </div>
      <div style={S.actionActions}>
        <select
          value={item.status}
          onChange={e => onUpsert({ id: item.id, status: e.target.value })}
          disabled={!!busy}
          style={{ ...S.selectSm, background: sc.bg, color: sc.fg, borderColor: sc.fg }}
        >
          <option value="open">Open</option>
          <option value="in_progress">In Progress</option>
          <option value="done">Done</option>
          <option value="blocked">Blocked</option>
        </select>
        <button onClick={() => setEditing(true)} style={S.linkBtn}>Edit</button>
        {extraAction}
        <button onClick={() => onDelete(item.id)} style={S.linkBtnDanger}>×</button>
      </div>
    </div>
  );
}

function NewActionItem({ meetingId, onAdd, busy }) {
  const [desc, setDesc]   = useState("");
  const [owner, setOwner] = useState("");
  const [due, setDue]     = useState("");
  const submit = async () => {
    if (!desc.trim()) return;
    await onAdd({ meeting_id: meetingId, description: desc.trim(), owner: owner || null, due_date: due || null });
    setDesc(""); setOwner(""); setDue("");
  };
  return (
    <div style={S.newItemRow}>
      <input placeholder="New action item…" value={desc} onChange={e => setDesc(e.target.value)}
             style={{ ...S.input, gridColumn: "1 / span 2" }}
             onKeyDown={e => e.key === "Enter" && submit()} />
      <input placeholder="Owner" value={owner} onChange={e => setOwner(e.target.value)} style={S.input} />
      <input type="date" value={due} onChange={e => setDue(e.target.value)} style={S.input} />
      <button onClick={submit} disabled={busy || !desc.trim()} style={S.btnPrimary}>Add</button>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// Meeting Notes — paste / upload / record → Claude extracts memories
// ═════════════════════════════════════════════════════════════════════════
function MeetingNotesCard({ notes, types, onOpenModal, onDeleteType }) {
  const [showTypes, setShowTypes] = useState(false);
  const recent = notes.slice(0, 8);
  const totalMemories = notes.reduce((s, n) => s + (n.memories_extracted || 0), 0);

  return (
    <div style={S.card}>
      <div style={S.cardHeader}>
        <div>
          <div style={S.cardTitle}>Meeting Notes → Jai-Man's Brain</div>
          <div style={S.notesSubtitle}>
            Paste Zoom transcripts, upload files, or record a meeting in-browser.
            Jai-Man extracts durable facts into his memory.
            {notes.length > 0 && (
              <> · <strong>{notes.length}</strong> note{notes.length === 1 ? "" : "s"} ingested ·{" "}
                <strong>{totalMemories}</strong> memories extracted</>
            )}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => setShowTypes(s => !s)} style={S.btn}>
            {showTypes ? "Hide types" : "Manage types"}
          </button>
          <button onClick={onOpenModal} style={S.btnPrimary}>+ Add Meeting Notes</button>
        </div>
      </div>

      {showTypes && (
        <div style={S.typesList}>
          {types.map(t => (
            <div key={t.id} style={S.typeRow}>
              <span style={S.typeName}>
                {t.name}
                <span style={S.typeSlug}>{t.slug}</span>
                {t.is_default && <span style={S.typeBuiltIn}>built-in</span>}
              </span>
              {!t.is_default && (
                <button onClick={() => onDeleteType(t.id)} style={S.linkBtnDanger}>delete</button>
              )}
            </div>
          ))}
          {types.length === 0 && (
            <div style={S.emptyText}>No types yet — loading…</div>
          )}
        </div>
      )}

      {recent.length === 0 ? (
        <div style={S.emptyText}>No meeting notes yet. Add your first one above.</div>
      ) : (
        <div style={{ marginTop: 8 }}>
          {recent.map(n => (
            <MeetingNoteRow key={n.id} note={n} />
          ))}
        </div>
      )}
    </div>
  );
}

function MeetingNoteRow({ note, compact }) {
  const [showTranscript, setShowTranscript] = useState(false);
  const [transcript, setTranscript] = useState(null);
  const [loadingTranscript, setLoadingTranscript] = useState(false);
  const [transcriptError, setTranscriptError] = useState("");

  const toggleTranscript = async () => {
    if (showTranscript) {
      setShowTranscript(false);
      return;
    }
    setShowTranscript(true);
    if (transcript !== null) return;
    setLoadingTranscript(true);
    setTranscriptError("");
    try {
      const r = await fetch(`${API}?action=get-meeting-note&id=${encodeURIComponent(note.id)}`).then(r => r.json());
      if (!r.ok) throw new Error(r.error || "Could not load transcript");
      setTranscript(r.note?.raw_transcript || "");
    } catch (e) {
      setTranscriptError(e.message);
    } finally {
      setLoadingTranscript(false);
    }
  };

  return (
    <div style={compact ? S.noteRowCompact : S.noteRow}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={S.noteHeader}>
            <span style={S.noteDate}>{fmtDateShort(note.meeting_date)}</span>
            {note.meeting_time && <span style={S.noteTime}>{note.meeting_time.slice(0, 5)}</span>}
            <span style={S.noteTypeBadge}>{note.type_name || note.type_slug || "Note"}</span>
            {note.source && <span style={S.noteSource}>{sourceLabel(note.source)}</span>}
            {note.linked_meeting_id && !compact && (
              <span style={S.noteLinked}>linked to meeting</span>
            )}
          </div>
          {note.title && <div style={S.noteTitle}>{note.title}</div>}
          {note.summary && <div style={S.noteSummary}>{note.summary}</div>}
          <div style={S.noteActions}>
            <button onClick={toggleTranscript} style={S.linkBtn}>
              {showTranscript ? "Hide transcript" : "View transcript"}
            </button>
            <a
              href={`/jaiman-learning?tag=meeting-notes&date=${note.meeting_date}`}
              style={S.noteMemoriesLink}
              title="View memories extracted from this note"
            >
              {note.memories_extracted || 0} memor{note.memories_extracted === 1 ? "y" : "ies"} →
            </a>
          </div>
        </div>
      </div>
      {showTranscript && (
        <div style={S.transcriptBox}>
          {loadingTranscript && <div style={{ fontSize: 12, color: "#8892a4" }}>Loading transcript…</div>}
          {transcriptError && <div style={{ fontSize: 12, color: "#fca5a5" }}>{transcriptError}</div>}
          {!loadingTranscript && !transcriptError && (
            transcript
              ? <pre style={S.transcriptPre}>{transcript}</pre>
              : <div style={{ fontSize: 12, color: "#8892a4", fontStyle: "italic" }}>
                  No transcript stored for this note (added before transcripts were saved).
                </div>
          )}
        </div>
      )}
    </div>
  );
}

function sourceLabel(s) {
  if (s === "paste") return "✎ pasted";
  if (s === "upload") return "📂 uploaded";
  if (s === "recording") return "🎙 recorded";
  return s;
}

function MeetingNotesModal({ types, onCreateType, onIngest, onClose }) {
  const todayIso = todayUTC().toISOString().slice(0, 10);
  const [date, setDate] = useState(todayIso);
  const [time, setTime] = useState("");
  const [typeId, setTypeId] = useState(types[0]?.id || "");
  const [title, setTitle] = useState("");
  const [inputMode, setInputMode] = useState("paste"); // 'paste' | 'upload' | 'record'
  const [transcript, setTranscript] = useState("");
  const [busy, setBusy] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [result, setResult] = useState(null);

  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [recElapsed, setRecElapsed] = useState(0);
  const mediaRecorderRef = useRef(null);
  const recChunksRef = useRef([]);
  const recTimerRef = useRef(null);
  const recStartRef = useRef(0);

  // New-type sub-form
  const [showNewType, setShowNewType] = useState(false);
  const [newTypeName, setNewTypeName] = useState("");

  useEffect(() => {
    if (!typeId && types.length > 0) setTypeId(types[0].id);
  }, [types, typeId]);

  useEffect(() => () => {
    // Cleanup recording on unmount
    if (recTimerRef.current) clearInterval(recTimerRef.current);
    const mr = mediaRecorderRef.current;
    if (mr && mr.state !== "inactive") mr.stop();
  }, []);

  const blobToBase64 = (blob) => new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const result = r.result;
      const idx = typeof result === "string" ? result.indexOf(",") : -1;
      resolve(idx >= 0 ? result.slice(idx + 1) : result);
    };
    r.onerror = () => reject(new Error("Could not read audio"));
    r.readAsDataURL(blob);
  });

  const transcribeAudio = async (blob, mimeType) => {
    setBusy("transcribing");
    setErrorMsg("");
    try {
      const audioBase64 = await blobToBase64(blob);
      const r = await apiFetch(CLAUDE_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "transcribeAudio", audioBase64, mimeType }),
      }).then(r => r.json());
      if (r.transcript) {
        setTranscript(prev => prev ? `${prev}\n\n${r.transcript}` : r.transcript);
      } else if (r.error) {
        throw new Error(r.error);
      } else {
        throw new Error("No transcript returned");
      }
    } catch (e) {
      setErrorMsg(`Transcription failed: ${e.message}`);
    } finally {
      setBusy("");
    }
  };

  const stripTimestamps = (text) => {
    // Strip WEBVTT/SRT timestamp lines and cue numbers; keep speaker text
    return text
      .replace(/^WEBVTT.*$/gm, "")
      .replace(/^\d+\s*$/gm, "")
      .replace(/^\d{1,2}:\d{2}(?::\d{2})?[.,]\d{1,3}\s*-->\s*\d{1,2}:\d{2}(?::\d{2})?[.,]\d{1,3}.*$/gm, "")
      .replace(/\n{3,}/g, "\n\n")
      .trim();
  };

  const onFileSelected = async (file) => {
    if (!file) return;
    setErrorMsg("");
    const name = file.name.toLowerCase();
    const isAudio = /\.(mp3|m4a|wav|webm|mp4|mpga|ogg|flac)$/i.test(name) || file.type.startsWith("audio/");
    if (isAudio) {
      if (file.size > 25 * 1024 * 1024) {
        setErrorMsg("Audio file exceeds 25MB Whisper limit. Trim or split it.");
        return;
      }
      await transcribeAudio(file, file.type || "audio/mpeg");
    } else {
      // Read as text
      try {
        const text = await file.text();
        setTranscript(prev => {
          const cleaned = stripTimestamps(text);
          return prev ? `${prev}\n\n${cleaned}` : cleaned;
        });
      } catch (e) {
        setErrorMsg(`Could not read file: ${e.message}`);
      }
    }
  };

  const startRecording = async () => {
    setErrorMsg("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const mr = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      recChunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) recChunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(recChunksRef.current, { type: mr.mimeType || "audio/webm" });
        if (blob.size > 0) {
          await transcribeAudio(blob, mr.mimeType || "audio/webm");
        }
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setIsRecording(true);
      recStartRef.current = Date.now();
      setRecElapsed(0);
      recTimerRef.current = setInterval(() => {
        setRecElapsed(Math.floor((Date.now() - recStartRef.current) / 1000));
      }, 1000);
    } catch (e) {
      setErrorMsg(`Could not start recording: ${e.message}. Make sure you've allowed microphone access.`);
    }
  };

  const stopRecording = () => {
    const mr = mediaRecorderRef.current;
    if (mr && mr.state !== "inactive") mr.stop();
    if (recTimerRef.current) { clearInterval(recTimerRef.current); recTimerRef.current = null; }
    setIsRecording(false);
  };

  const handleCreateType = async () => {
    const name = newTypeName.trim();
    if (!name) return;
    try {
      const created = await onCreateType(name);
      if (created?.id) setTypeId(created.id);
      setNewTypeName("");
      setShowNewType(false);
    } catch (e) {
      setErrorMsg(e.message);
    }
  };

  const handleSave = async () => {
    setErrorMsg("");
    if (!transcript.trim() || transcript.trim().length < 20) {
      setErrorMsg("Add some transcript text first (at least 20 characters).");
      return;
    }
    if (!typeId) {
      setErrorMsg("Pick a meeting type first.");
      return;
    }
    setBusy("saving");
    try {
      const r = await onIngest({
        meeting_date: date,
        meeting_time: time || null,
        type_id: typeId,
        title: title.trim() || null,
        source: inputMode === "paste" ? "paste" : (inputMode === "upload" ? "upload" : "recording"),
        transcript,
      });
      setResult(r);
    } catch (e) {
      setErrorMsg(e.message);
    } finally {
      setBusy("");
    }
  };

  const recElapsedLabel = `${String(Math.floor(recElapsed / 60)).padStart(2, "0")}:${String(recElapsed % 60).padStart(2, "0")}`;
  const recWarning = recElapsed >= 20 * 60;

  if (result) {
    const sel = types.find(t => t.id === typeId);
    return (
      <div style={S.modalBackdrop} onClick={onClose}>
        <div style={S.modal} onClick={e => e.stopPropagation()}>
          <div style={S.modalHeader}>
            <div style={S.modalTitle}>✓ Saved & Sent to Jai-Man</div>
            <button onClick={onClose} style={S.modalClose}>×</button>
          </div>
          <div style={{ padding: 20 }}>
            <div style={{ fontSize: 14, color: "#e8eaf0", marginBottom: 12 }}>
              Extracted <strong>{result.memories_extracted}</strong> memor{result.memories_extracted === 1 ? "y" : "ies"} from your {(sel?.name || "meeting").toLowerCase()} on {fmtDateLong(date)}.
            </div>
            {result.summary && (
              <div style={S.summaryBox}>
                <div style={S.summaryLabel}>Summary</div>
                <div style={{ fontSize: 13, color: "#e8eaf0", lineHeight: 1.5 }}>{result.summary}</div>
              </div>
            )}
            {result.linked_meeting_id && (
              <div style={S.linkedHint}>📎 Linked to the existing coaching meeting on this date.</div>
            )}
            <div style={S.modalFooter}>
              <a
                href={`/jaiman-learning?tag=meeting-notes&date=${date}`}
                style={S.btnPrimary}
              >
                View Memories in Jai-Man Learning →
              </a>
              <button onClick={onClose} style={S.btn}>Close</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={S.modalBackdrop} onClick={onClose}>
      <div style={S.modal} onClick={e => e.stopPropagation()}>
        <div style={S.modalHeader}>
          <div style={S.modalTitle}>Add Meeting Notes</div>
          <button onClick={onClose} style={S.modalClose}>×</button>
        </div>

        <div style={S.modalBody}>
          {/* Date / time / type / title */}
          <div style={S.modalGrid}>
            <div>
              <div style={S.label}>Date</div>
              <input type="date" value={date} onChange={e => setDate(e.target.value)} style={S.input} />
            </div>
            <div>
              <div style={S.label}>Time (optional)</div>
              <input type="time" value={time} onChange={e => setTime(e.target.value)} style={S.input} />
            </div>
          </div>

          <div style={{ marginTop: 10 }}>
            <div style={S.label}>Type</div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <select value={typeId} onChange={e => setTypeId(e.target.value)} style={{ ...S.select, flex: 1, minWidth: 220 }}>
                {types.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
              <button onClick={() => setShowNewType(s => !s)} style={S.btn}>
                {showNewType ? "Cancel" : "+ New type"}
              </button>
            </div>
            {showNewType && (
              <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                <input
                  placeholder="e.g. Vendor Visit, Quarterly Review…"
                  value={newTypeName}
                  onChange={e => setNewTypeName(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && handleCreateType()}
                  style={S.input}
                />
                <button onClick={handleCreateType} disabled={!newTypeName.trim()} style={S.btnPrimary}>
                  Save type
                </button>
              </div>
            )}
          </div>

          <div style={{ marginTop: 10 }}>
            <div style={S.label}>Title (optional)</div>
            <input
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="e.g. Q2 strategy session with Kyle"
              style={S.input}
            />
          </div>

          {/* Input mode tabs */}
          <div style={{ marginTop: 16, marginBottom: 10 }}>
            <div style={S.tabRow}>
              {["paste", "upload", "record"].map(m => (
                <button
                  key={m}
                  onClick={() => setInputMode(m)}
                  style={inputMode === m ? S.tabActive : S.tab}
                >
                  {m === "paste" ? "Paste" : m === "upload" ? "Upload File" : "Record"}
                </button>
              ))}
            </div>
          </div>

          {/* Upload mode */}
          {inputMode === "upload" && (
            <div style={S.uploadBox}>
              <div style={{ fontSize: 12, color: "#8892a4", marginBottom: 8 }}>
                Accepts text transcripts (.txt, .vtt, .srt, .md) or audio (.mp3, .m4a, .wav, .webm — under 25MB).
                Audio gets transcribed via Whisper before extraction.
              </div>
              <input
                type="file"
                accept=".txt,.vtt,.srt,.md,.mp3,.m4a,.wav,.webm,.mp4,.mpga,.ogg,.flac,audio/*,text/*"
                onChange={e => onFileSelected(e.target.files?.[0])}
                style={{ color: "#e8eaf0" }}
              />
            </div>
          )}

          {/* Record mode */}
          {inputMode === "record" && (
            <div style={S.recordBox}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                {!isRecording ? (
                  <button onClick={startRecording} disabled={busy === "transcribing"} style={S.recordBtn}>
                    ● Start Recording
                  </button>
                ) : (
                  <button onClick={stopRecording} style={S.recordBtnStop}>
                    ■ Stop · {recElapsedLabel}
                  </button>
                )}
                {busy === "transcribing" && <span style={{ fontSize: 12, color: "#8892a4" }}>Transcribing…</span>}
              </div>
              {recWarning && (
                <div style={{ fontSize: 11, color: "#fbbf24", marginTop: 6 }}>
                  ⚠ Approaching Whisper's 25-minute limit. Stop and start a new recording if it's a long meeting.
                </div>
              )}
              <div style={{ fontSize: 11, color: "#5e6675", marginTop: 6 }}>
                Browser will ask for microphone permission. Recording is uploaded only when you stop — nothing is sent live.
              </div>
            </div>
          )}

          {/* Transcript editor (shared across all modes) */}
          <div style={{ marginTop: 12 }}>
            <div style={S.label}>
              Transcript
              {transcript.length > 0 && (
                <span style={{ color: "#5e6675", marginLeft: 8 }}>
                  {transcript.length.toLocaleString()} chars
                </span>
              )}
            </div>
            <textarea
              placeholder={
                inputMode === "paste"
                  ? "Paste your Zoom transcript, meeting notes, or stream-of-consciousness braindump here…"
                  : inputMode === "upload"
                    ? "Pick a file above — transcript will appear here. You can edit before saving."
                    : "Click ● Start Recording above. Transcript appears here when you stop. You can edit before saving."
              }
              value={transcript}
              onChange={e => setTranscript(e.target.value)}
              style={{ ...S.textarea, minHeight: 200 }}
              rows={12}
            />
          </div>

          {errorMsg && <div style={S.errorBanner}>{errorMsg}</div>}
        </div>

        <div style={S.modalFooter}>
          <button onClick={onClose} style={S.btn} disabled={busy === "saving"}>Cancel</button>
          <button
            onClick={handleSave}
            disabled={busy === "saving" || busy === "transcribing" || !transcript.trim()}
            style={S.btnPrimary}
          >
            {busy === "saving" ? "Extracting memories…" : "Save & Send to Jai-Man"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// Styles — match Coach.jsx visual language
// ═════════════════════════════════════════════════════════════════════════
const S = {
  page: { padding: 24, color: "#e8eaf0", fontFamily: "'Roboto', sans-serif", maxWidth: 1100, margin: "0 auto" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20, flexWrap: "wrap", gap: 12 },
  backBtn: { background: "none", border: "none", color: "#a5ce3a", fontSize: 13, cursor: "pointer", fontFamily: "inherit", padding: "0 0 6px 0" },
  title: { fontSize: 28, fontWeight: 700, fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.02em" },
  subtitle: { fontSize: 13, color: "#8892a4", marginTop: 4 },
  headerActions: { display: "flex", gap: 8 },

  btn: { padding: "8px 14px", background: "#1e2328", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 6, color: "#f0f0f0", fontSize: 13, cursor: "pointer", fontFamily: "inherit" },
  btnPrimary: { padding: "8px 14px", background: "#a5ce3a", border: "none", borderRadius: 6, color: "#0d0f12", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" },
  linkBtn: { background: "none", border: "none", color: "#a5ce3a", cursor: "pointer", fontSize: 12, fontFamily: "inherit", padding: "4px 6px" },
  linkBtnDanger: { background: "none", border: "none", color: "#fca5a5", cursor: "pointer", fontSize: 12, fontFamily: "inherit", padding: "4px 6px" },
  bannerClose: { background: "none", border: "none", color: "inherit", cursor: "pointer", fontSize: 18, lineHeight: 1, padding: 0, marginLeft: 12 },

  errorBanner: { background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)", color: "#fca5a5", padding: "10px 14px", borderRadius: 8, marginBottom: 12, fontSize: 13, display: "flex", justifyContent: "space-between", alignItems: "center" },
  statusBanner: { background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.25)", color: "#86efac", padding: "10px 14px", borderRadius: 8, marginBottom: 12, fontSize: 13, display: "flex", justifyContent: "space-between", alignItems: "center" },

  card: { background: "#161a20", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12, padding: 16, marginBottom: 16 },
  cardHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 10 },
  cardTitle: { fontSize: 14, fontWeight: 700, color: "#e8eaf0", letterSpacing: "0.01em" },
  emptyText: { fontSize: 13, color: "#8892a4", padding: "8px 4px" },
  emptyCard: { background: "#161a20", border: "1px dashed rgba(255,255,255,0.1)", borderRadius: 12, padding: 24, textAlign: "center", fontSize: 13, color: "#8892a4", marginBottom: 16 },

  sectionHeader: { display: "flex", alignItems: "baseline", gap: 8, marginTop: 24, marginBottom: 10 },
  sectionTitle: { fontSize: 18, fontWeight: 700, fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.04em", color: "#e8eaf0" },
  sectionCount: { fontSize: 12, color: "#5e6675", padding: "1px 8px", background: "#1e2328", borderRadius: 10, border: "1px solid rgba(255,255,255,0.06)" },

  // Tabs
  tabRow: { display: "flex", gap: 4, background: "#1e2328", padding: 3, borderRadius: 8, border: "1px solid rgba(255,255,255,0.06)" },
  tab: { padding: "6px 14px", background: "transparent", border: "none", borderRadius: 6, color: "#8892a4", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" },
  tabActive: { padding: "6px 14px", background: "#a5ce3a", border: "none", borderRadius: 6, color: "#0d0f12", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" },

  // Inputs
  input: { padding: "8px 10px", background: "#1e2328", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 6, color: "#f0f0f0", fontSize: 13, fontFamily: "inherit", boxSizing: "border-box", width: "100%" },
  textarea: { padding: "10px 12px", background: "#1e2328", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 6, color: "#f0f0f0", fontSize: 13, fontFamily: "inherit", width: "100%", boxSizing: "border-box", resize: "vertical", lineHeight: 1.5 },
  select: { padding: "8px 10px", background: "#1e2328", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 6, color: "#f0f0f0", fontSize: 13, fontFamily: "inherit" },
  selectSm: { padding: "5px 8px", background: "#1e2328", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 5, color: "#f0f0f0", fontSize: 12, fontFamily: "inherit", cursor: "pointer" },
  label: { fontSize: 12, color: "#8892a4" },
  savingHint: { fontSize: 11, color: "#a5ce3a", marginTop: 4 },

  // Goals
  goalsControls: { display: "flex", gap: 10, alignItems: "center", marginBottom: 14, flexWrap: "wrap" },
  goalsProgress: { display: "flex", alignItems: "center", gap: 10, flex: 1, minWidth: 200 },
  goalsProgressLabel: { fontSize: 12, color: "#8892a4", whiteSpace: "nowrap" },
  goalsProgressBar: { flex: 1, height: 6, background: "#1e2328", borderRadius: 3, overflow: "hidden" },
  goalsProgressFill: { height: "100%", background: "linear-gradient(90deg, #a5ce3a, #86efac)", transition: "width 0.3s" },

  goalRow: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 12px", borderRadius: 8, background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.04)", marginBottom: 6, flexWrap: "wrap", gap: 8 },
  goalMain: { flex: 1, minWidth: 200 },
  goalDesc: { fontSize: 13, color: "#e8eaf0", fontWeight: 500 },
  goalMeta: { fontSize: 11, color: "#8892a4", marginTop: 3, display: "flex", gap: 10, flexWrap: "wrap" },
  goalActions: { display: "flex", alignItems: "center", gap: 6 },

  statusPill: { fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 12, textTransform: "uppercase", letterSpacing: "0.04em" },
  chip: { padding: "5px 10px", background: "#1e2328", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 5, color: "#8892a4", fontSize: 11, cursor: "pointer", fontFamily: "inherit" },
  chipActive: { padding: "5px 10px", background: "#a5ce3a", border: "1px solid #a5ce3a", borderRadius: 5, color: "#0d0f12", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" },

  // Editor row (inline goal/action editor)
  editorRow: { background: "rgba(165,206,58,0.04)", border: "1px solid rgba(165,206,58,0.2)", borderRadius: 8, padding: 12, marginBottom: 8 },
  editorGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 },
  editorActions: { display: "flex", gap: 6, justifyContent: "flex-end", marginTop: 10 },

  // Meeting cards
  meetingCard: { background: "#161a20", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12, marginBottom: 10, overflow: "hidden", transition: "border-color 0.2s" },
  meetingHead: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 16px", cursor: "pointer", gap: 10 },
  meetingDate: { fontSize: 14, fontWeight: 700, color: "#e8eaf0" },
  meetingSubdate: { fontSize: 11, color: "#8892a4", marginTop: 2 },
  chevron: { fontSize: 14, color: "#8892a4", width: 14, textAlign: "center" },
  meetingFootHint: { padding: "0 16px 12px 42px", fontSize: 11, color: "#5e6675" },
  meetingBody: { padding: "0 16px 16px 16px", borderTop: "1px solid rgba(255,255,255,0.06)" },

  // Section within a meeting card
  section: { marginTop: 14, paddingTop: 14, borderTop: "1px solid rgba(255,255,255,0.04)" },
  sectionHead: { display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 8 },
  sectionTitleSm: { fontSize: 13, fontWeight: 700, color: "#a5ce3a", letterSpacing: "0.02em" },
  sectionMinutes: { fontSize: 10, color: "#5e6675", textTransform: "uppercase", letterSpacing: "0.05em" },

  statusRow: { display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", paddingBottom: 4, paddingTop: 4 },

  // KPI cards
  kpiGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 },
  kpiFreshness: { gridColumn: "1 / -1", display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8, fontSize: 11, color: "#8892a4", marginTop: 2 },
  kpiStale: { color: "#fbbf24", fontWeight: 600 },
  kpiCard: { background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 10, padding: 12 },
  kpiLabel: { fontSize: 11, color: "#8892a4", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 },
  kpiValue: { fontSize: 22, fontWeight: 800, color: "#f0f0f0" },
  kpiSub: { fontSize: 11, color: "#8892a4", marginTop: 4 },

  miniTable: { width: "100%", borderCollapse: "collapse", marginTop: 8, fontSize: 12 },
  miniTh: { padding: "6px 8px", textAlign: "left", color: "#8892a4", fontWeight: 600, fontSize: 10, textTransform: "uppercase", borderBottom: "1px solid rgba(255,255,255,0.08)" },
  miniTd: { padding: "6px 8px", color: "#e8eaf0", borderBottom: "1px solid rgba(255,255,255,0.04)", fontVariantNumeric: "tabular-nums" },

  // Goal check-in (inside meeting card)
  checkInGroupTitle: { fontSize: 10, color: "#5e6675", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6, fontWeight: 700 },
  checkInRow: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 10px", borderRadius: 6, background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.04)", marginBottom: 5, gap: 8 },
  checkInDesc: { fontSize: 13, color: "#e8eaf0" },
  checkInMeta: { fontSize: 11, color: "#8892a4", marginTop: 2 },

  // Action items
  actionRow: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 10px", borderRadius: 6, background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.04)", marginBottom: 5, gap: 8, flexWrap: "wrap" },
  actionRowEditing: { display: "grid", gridTemplateColumns: "2fr 1fr", gap: 8, padding: 12, borderRadius: 6, background: "rgba(165,206,58,0.04)", border: "1px solid rgba(165,206,58,0.2)", marginBottom: 5 },
  actionDesc: { fontSize: 13, color: "#e8eaf0" },
  actionMeta: { fontSize: 11, color: "#8892a4", marginTop: 2, display: "flex", gap: 6, flexWrap: "wrap" },
  actionActions: { display: "flex", alignItems: "center", gap: 4, flexShrink: 0 },

  newItemRow: { display: "grid", gridTemplateColumns: "2fr 1fr 1fr auto", gap: 6, alignItems: "center" },

  // Meeting-notes ingestion
  notesSubtitle: { fontSize: 12, color: "#8892a4", marginTop: 4, lineHeight: 1.5, maxWidth: 720 },
  typesList: { marginTop: 10, marginBottom: 12, padding: 10, background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 8 },
  typeRow: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "5px 8px", fontSize: 13 },
  typeName: { display: "flex", alignItems: "center", gap: 8, color: "#e8eaf0" },
  typeSlug: { fontSize: 10, color: "#5e6675", fontFamily: "monospace", padding: "1px 5px", background: "#1e2328", borderRadius: 3 },
  typeBuiltIn: { fontSize: 10, color: "#a5ce3a", padding: "1px 6px", background: "rgba(165,206,58,0.1)", borderRadius: 3, fontWeight: 600 },

  noteRow: { padding: "10px 12px", borderRadius: 8, background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.04)", marginBottom: 6 },
  noteRowCompact: { padding: "8px 10px", borderRadius: 6, background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.04)", marginBottom: 5 },
  noteActions: { display: "flex", alignItems: "center", gap: 12, marginTop: 6 },
  transcriptBox: { marginTop: 8, padding: 10, background: "#0d0f12", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 6, maxHeight: 400, overflowY: "auto" },
  transcriptPre: { fontFamily: "'Roboto Mono', 'Consolas', monospace", fontSize: 12, color: "#cbd5e1", whiteSpace: "pre-wrap", wordBreak: "break-word", margin: 0, lineHeight: 1.5 },
  noteHeader: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 2 },
  noteDate: { fontSize: 12, fontWeight: 700, color: "#e8eaf0" },
  noteTime: { fontSize: 11, color: "#8892a4", fontVariantNumeric: "tabular-nums" },
  noteTypeBadge: { fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 4, background: "rgba(96,165,250,0.12)", color: "#93c5fd", textTransform: "uppercase", letterSpacing: "0.04em" },
  noteSource: { fontSize: 10, color: "#5e6675" },
  noteLinked: { fontSize: 10, color: "#a5ce3a" },
  noteTitle: { fontSize: 13, color: "#e8eaf0", fontWeight: 600, marginTop: 2 },
  noteSummary: { fontSize: 12, color: "#8892a4", marginTop: 3, lineHeight: 1.4 },
  noteMemoriesLink: { fontSize: 11, color: "#a5ce3a", textDecoration: "none", whiteSpace: "nowrap", marginLeft: "auto" },

  // Modal
  modalBackdrop: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 },
  modal: { background: "#161a20", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, width: "100%", maxWidth: 720, maxHeight: "90vh", display: "flex", flexDirection: "column", overflow: "hidden" },
  modalHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 20px", borderBottom: "1px solid rgba(255,255,255,0.06)" },
  modalTitle: { fontSize: 18, fontWeight: 700, fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.02em", color: "#e8eaf0" },
  modalClose: { background: "none", border: "none", color: "#8892a4", fontSize: 24, lineHeight: 1, cursor: "pointer", padding: "0 4px" },
  modalBody: { padding: 20, overflowY: "auto", flex: 1 },
  modalFooter: { display: "flex", gap: 8, justifyContent: "flex-end", padding: "12px 20px", borderTop: "1px solid rgba(255,255,255,0.06)" },
  modalGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 },

  uploadBox: { padding: 14, background: "rgba(255,255,255,0.02)", border: "1px dashed rgba(255,255,255,0.12)", borderRadius: 8 },
  recordBox: { padding: 14, background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 8 },
  recordBtn: { padding: "10px 18px", background: "#ef4444", border: "none", borderRadius: 6, color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" },
  recordBtnStop: { padding: "10px 18px", background: "#1e2328", border: "1px solid #ef4444", borderRadius: 6, color: "#fca5a5", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", fontVariantNumeric: "tabular-nums" },

  summaryBox: { background: "rgba(165,206,58,0.05)", border: "1px solid rgba(165,206,58,0.2)", borderRadius: 8, padding: 12, marginBottom: 12 },
  summaryLabel: { fontSize: 10, fontWeight: 700, color: "#a5ce3a", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 },
  linkedHint: { fontSize: 12, color: "#a5ce3a", marginBottom: 12 },
};
